/**
 * Provider-independent Book model. The UI (BookCard, BookDetailModal,
 * Bookshelf) only ever consumes this shape — never a raw Open Library
 * response — so a future ScribbleReads backend can replace the provider
 * underneath without any UI changes.
 *
 * @typedef {Object} Book
 * @property {string} id                     - Stable id ("/works/OL45804W" for Open Library)
 * @property {string} title
 * @property {string[]} authors
 * @property {string} [coverUrl]
 * @property {number} [coverId]
 * @property {string[]} [isbn10]
 * @property {string[]} [isbn13]
 * @property {number} [publishYear]
 * @property {number} [firstPublishYear]
 * @property {string[]} [subjects]
 * @property {string} [publisher]
 * @property {string} [description]
 * @property {string} [openLibraryWorkId]
 * @property {string} [openLibraryEditionId]
 * @property {'openlibrary'} source
 */

const TONES = ['coral', 'yellow', 'sage', 'blue'];

/**
 * Deterministic tone for the gradient-spine cover fallback, so the same
 * book always gets the same color instead of a random one on every render.
 */
export function pickTone(id) {
  const str = String(id ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return TONES[hash % TONES.length];
}

/**
 * One stable id for a book regardless of where it came from — an already-
 * shelved/seed-catalog book keeps its own id; a live Open Library book
 * (or one not yet shelved) gets the sanitized `ol_<workId>` form, so
 * looking a book up by this id finds it whether it's shelved yet or not.
 * This generalizes the `ol_${workId}` derivation that used to be
 * duplicated inline in BookCard.jsx and BookDetailModal.jsx.
 */
export function getCanonicalBookId(book) {
  if (!book) return null;
  if (book.id && book.source !== 'openlibrary' && !book.openLibraryWorkId) {
    return book.id;
  }
  let workId = book.openLibraryWorkId || String(book.id ?? '').replace('/works/', '');
  workId = workId.replace(/^ol_/, '');
  return workId
    ? `ol_${workId}`.replace(/[^\w-]/g, '_')
    : String(book.id ?? '').replace(/[^\w-]/g, '_');
}

/**
 * Recovers an Open Library work id from a canonical `ol_<workId>` id, so a
 * Book Details route can fetch live data when a book isn't shelved/seeded
 * locally. Round-trips exactly because OL work ids are plain alphanumeric
 * (e.g. "OL45804W"), which getCanonicalBookId's sanitizing regex leaves
 * untouched.
 */
export function extractOpenLibraryWorkId(canonicalId) {
  if (!canonicalId || !canonicalId.startsWith('ol_')) return null;
  return canonicalId.slice(3);
}

/**
 * The Book Details route for a book, from either an id or a Book object.
 */
export function bookHref(idOrBook) {
  const id = typeof idOrBook === 'string' ? idOrBook : getCanonicalBookId(idOrBook);
  return id ? `/app/books/${id}` : '/app/explore';
}

/**
 * Converts a normalized Book into the shape Bookshelf's own schema expects
 * (see components/bookshelf/modals/AddBookModal.jsx), so "Add to TBR" from
 * search results behaves identically to adding from the existing catalog.
 */
export function toShelfBook(book, shelfId) {
  return {
    id: getCanonicalBookId(book),
    title: book.title,
    author: book.authors?.[0] ?? 'Unknown Author',
    tone: pickTone(book.id),
    coverUrl: book.coverUrl ?? undefined,
    genres: book.subjects?.slice(0, 3) ?? [],
    totalPages: undefined,
    status: shelfId === 'shelf_reading' ? 'reading' : 'tbr',
    isFavorite: shelfId === 'shelf_favorites',
    dateAdded: Date.now(),
  };
}
